/**
 * Terminal-state toasts for transfer jobs (spec §7.2 item 4). Each toast reads
 * the snapshot only: file name from the destination, the message from the
 * event's `lastError` (falls back to `job.errors`). `interrupted` is a
 * reconciliation state, not a transition, so it never toasts; a dismissed save
 * dialog never reaches here (no job exists).
 */
import { toast } from '../../../components/ui/toaster';
import { aclJobSummary } from '../../../core/engines/azureStorageAclJobText';
import type { TransferProgressEvent } from '../../../types/azureStorageTransfers';
import { transferFileName } from '../model/transferJobRules';
import { openTransfersPanel } from '../services/openTransfersPanel';

export const SOURCE_CHANGED_CODE = 'SourceChanged';

function viewTransfersAction(): { label: string; onClick: () => void } {
  return {
    label: 'View transfers',
    onClick: () => openTransfersPanel('toast'),
  };
}

/** The job's noun — the same for every storage service (a file-share upload is an "Upload"; S8: an `acl` job an "Access-control change"). */
const nounOf = (kind: TransferProgressEvent['job']['kind']): string => kind === 'upload' ? 'Upload' : kind === 'delete' ? 'Delete' : kind === 'copy' ? 'Copy' : kind === 'export' ? 'Export' : kind === 'acl' ? 'Access-control change' : 'Download';

function failureMessage(event: TransferProgressEvent): string {
  return event.lastError?.message ?? event.job.errors[0]?.message ?? `The ${nounOf(event.job.kind).toLowerCase()} did not finish.`;
}

/** Shows the toast for a terminal snapshot; returns whether one was shown. */
export function showTransferToast(event: TransferProgressEvent): boolean {
  const { job } = event;
  // S8: an access-control job is described by its summary (the honest cancel / partial wording), not by a file name.
  const acl = job.kind === 'acl' ? aclJobSummary(job) : undefined;
  const name = transferFileName(job);
  const noun = nounOf(job.kind);
  switch (job.state) {
    case 'completed':
      toast.success({ title: `${noun} complete`, description: acl ?? name, action: viewTransfersAction(), testId: 'transfer-toast-completed' });
      return true;
    case 'failed':
      toast.error({ title: `${noun} failed`, description: acl ?? `${name}: ${failureMessage(event)}`, action: viewTransfersAction(), testId: 'transfer-toast-failed' });
      return true;
    case 'cancelled':
      toast.default({ title: `${noun} cancelled`, description: acl ?? name, action: viewTransfersAction(), testId: 'transfer-toast-cancelled' });
      return true;
    case 'partial':
      toast.warning({ title: `${noun} partly completed`, description: acl ?? `${name}: ${failureMessage(event)}`, action: viewTransfersAction(), testId: 'transfer-toast-partial' });
      return true;
    default:
      return false;
  }
}

/** The non-error `SourceChanged` notice (main restarted the item from 0 because the remote source — blob or file — changed). */
export function showTransferNoticeToast(event: TransferProgressEvent): boolean {
  if (event.lastError?.code !== SOURCE_CHANGED_CODE) return false;
  toast.warning({
    title: 'Download restarted: source changed',
    description: `${transferFileName(event.job)} changed while the download was paused; it starts again from the beginning.`,
    testId: 'transfer-toast-source-changed',
  });
  return true;
}
